import {
    Box,
    Button,
    capitalize,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {FreeDay} from "../../api";
import EditIcon from "@mui/icons-material/Edit";
import {format, parseISO} from "date-fns";
import {de} from "date-fns/locale";
import {usePermissions} from "../PermissionsContext";
import FreeDayDialog from "./FreeDayDialog";
import {useGetApi} from "../../client";
import DeleteFreeDaysButton from "./DeleteFreeDaysButton";
import DataTable from "../data/DataTable";
import useCustomFilters from "../data/useCustomFilters";

export type FreeDaysFilter = {
    search?: string;
    filtered?: string;
    year?: number;
};

export type Props = Omit<DataRequest<FreeDaysFilter>, "filter"> &
    Partial<Pick<DataRequest<FreeDaysFilter>, "filter">>;

const tableName: string = "freeDays"

export default function FreeDaysTable({...input}: Props) {
    const request = useDataRequest<FreeDaysFilter>({filter: {}, ...input});
    const [filteredState, setFilteredState] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingFreeDay, setEditingFreeDay] = useState<FreeDay | null>(null);

    const handleCreate = useCallback(() => {
        setEditingFreeDay(null);
        setDialogOpen(true);
    }, []);

    const handleEdit = useCallback((freeDay: FreeDay) => {
        setEditingFreeDay(freeDay);
        setDialogOpen(true);
    }, []);

    const handleDialogClose = useCallback(() => {
        setDialogOpen(false);
        setEditingFreeDay(null);
    }, []);

    const handleDialogSuccess = useCallback(() => {
        handleDialogClose();
    }, [handleDialogClose]);

    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
                onCreate={handleCreate}
            />
            <FreeDaysResults
                request={request}
                setFilteredState={setFilteredState}
                onEdit={handleEdit}
            />

            <FreeDayDialog
                open={dialogOpen}
                freeDay={editingFreeDay}
                onClose={handleDialogClose}
                onSuccess={handleDialogSuccess}
            />
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                        onCreate,
                    }: {
    request: DataRequestState<FreeDaysFilter>;
    filteredStateFilter: string;
    onCreate: () => void;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<FreeDaysFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
            year: new Date().getFullYear(),
        }
    });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const permissions = usePermissions();
    const {t: translation} = useT("freedays");

    const canWrite = permissions.includes("freedays:write");

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            year: currentCustomFilter.year,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
            }}
        >
            <Stack direction={"row"} gap={2}>
                <SearchField
                    value={currentCustomFilter.search}
                    onChange={(val) => setCustomField("search", val)}
                />
                <FormControl size="small" sx={{width: "120px"}}>
                    <InputLabel>{capitalize(translation("freeDays.year"))}</InputLabel>
                    <Select
                        label={capitalize(translation("freeDays.year"))}
                        size="small"
                        value={currentCustomFilter.year ?? new Date().getFullYear()}
                        onChange={(e) => setCustomField("year", e.target.value as number)}
                    >
                        {Array.from({length: 20}, (_, i) => new Date().getFullYear() - 10 + i).map((year) => (
                            <MenuItem key={year} value={year}>
                                {year}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>
            {canWrite && (
                <Button
                    variant="contained"
                    color={"secondary"}
                    onClick={onCreate}
                    size={isMobile ? "small" : "medium"}
                >
                    {capitalize(translation("freeDays.create"))}
                </Button>
            )}
        </Box>
    );
}

function FreeDaysResults({
                             request,
                             setFilteredState,
                             onEdit,
                         }: {
    request: DataRequestState<FreeDaysFilter>;
    setFilteredState: (value: string) => void;
    onEdit: (freeDay: FreeDay) => void;
}) {
    const getApi = useGetApi();
    const loadFreeDays: DataLoader<FreeDaysFilter, FreeDay> = useCallback(
        async (params) => (await getApi()).freeDays.getFreeDays({...params}),
        [getApi]
    );

    return (
        <DataTable
            columns={useColumns(onEdit)}
            request={request}
            queryKey={[tableName]}
            loadData={loadFreeDays}
            name={tableName}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(
    onEdit: (freeDay: FreeDay) => void,
): Array<GridColDef<FreeDay>> {
    const {t: translation} = useT("freedays");

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const permissions = usePermissions();
    const canWrite = permissions.includes("freedays:write");

    return useMemo(
        () => [
            {
                field: "label",
                headerName: capitalize(translation("label")),
                minWidth: 300,
            },
            {
                field: "dateFrom",
                headerName: capitalize(translation("freeDays.dateFrom")),
                minWidth: 120,
                type: "date",
                filterable: false,
                valueFormatter: (params) => {
                    if (!params) return "";
                    try {
                        return format(parseISO(params), "dd.MM.yyyy", {locale: de});
                    } catch {
                        return params;
                    }
                },
            },
            {
                field: "dateTo",
                headerName: capitalize(translation("freeDays.dateTo")),
                minWidth: 120,
                type: "date",
                filterable: false,
                valueFormatter: (params) => {
                    if (!params) return "";
                    try {
                        return format(parseISO(params), "dd.MM.yyyy", {locale: de});
                    } catch {
                        return params;
                    }
                },
            },
            {
                field: "days",
                headerName: capitalize(translation("freeDays.days")),
                minWidth: 80,
                align: "right",
                headerAlign: "right",
            },
            ...(canWrite
                ? [
                    {
                        field: "actions",
                        headerName: capitalize(translation("freeDays.actions")),
                        flex: isMobile ? 1 : 0.5,
                        minWidth: isMobile ? 120 : 100,
                        sortable: false,
                        renderCell: (params: any) => {
                            const freeDay = params.row as FreeDay;
                            return (
                                <Box>
                                    <IconButton
                                        size="small"
                                        onClick={() => onEdit(freeDay)}
                                        aria-label={capitalize(translation("freeDays.edit"))}
                                    >
                                        <EditIcon/>
                                    </IconButton>
                                    <DeleteFreeDaysButton id={freeDay.id}/>
                                </Box>
                            );
                        },
                    },
                ]
                : []),
        ],
        [canWrite, isMobile, onEdit, translation]
    );
}